// Volume Status
export enum VolumeStatus {
  Available = 'Available',
  InUse = 'InUse',
  Pending = 'Pending',
  Expanding = 'Expanding',
  Deleting = 'Deleting',
  Error = 'Error',
}

// Health Status
export enum VolumeHealthStatus {
  Healthy = 'Healthy',
  Warning = 'Warning',
  Degraded = 'Degraded',
  Error = 'Error',
}

// Snapshot Status
export enum SnapshotStatus {
  Creating = 'Creating',
  Ready = 'Ready',
  Deleting = 'Deleting',
  Error = 'Error',
  Restoring = 'Restoring',
}

// Access Modes (Kubernetes PVC access modes)
export type AccessMode = 'ReadWriteOnce' | 'ReadWriteMany' | 'ReadOnlyMany' | 'ReadWriteOncePod';

// Volume Mode
export type VolumeMode = 'Filesystem' | 'Block';

// Storage Class Tier
export type StorageClassTier = 'standard-ssd' | 'premium-ssd' | 'nvme' | 'archive-hdd';

// Schedule Frequency
export type ScheduleFrequency = 'hourly' | 'daily' | 'weekly' | 'custom';

// Attached Instance Info
export interface AttachedInstance {
  instanceId: string;
  instanceName: string;
  mountPath: string;
  attachedAt: number;
}

// Volume Health Information
export interface VolumeHealth {
  status: VolumeHealthStatus;
  message?: string;
  lastChecked: number;
  alerts?: VolumeAlert[];
}

// Volume Alert
export interface VolumeAlert {
  id: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: number;
  acknowledged: boolean;
}

// Performance Metrics
export interface PerformanceMetrics {
  iops: {
    read: number;
    write: number;
    total: number;
  };
  throughput: {
    read: number; // MB/s
    write: number; // MB/s
    total: number; // MB/s
  };
  latency: {
    read: number; // ms
    write: number; // ms
    average: number; // ms
  };
  timestamp: number;
}

// Storage Class Definition
export interface StorageClass {
  id: string;
  name: string;
  tier: StorageClassTier;
  displayName: string;
  description: string;
  iopsRange: { min: number; max: number };
  throughputRange: { min: number; max: number }; // MB/s
  pricePerGBMonth: number; // USD
  isDefault: boolean;
  provisioner: string;
  reclaimPolicy: 'Delete' | 'Retain';
  allowVolumeExpansion: boolean;
  volumeBindingMode: 'Immediate' | 'WaitForFirstConsumer';
}

// Core Volume Interface
export interface Volume {
  id: string;
  name: string;
  pvcName: string;
  pvName: string;
  namespace: string;
  status: VolumeStatus;
  requestedSize: number; // GB
  allocatedSize: number; // GB
  usedSize: number; // GB
  storageClass: StorageClass;
  accessMode: AccessMode;
  volumeMode: VolumeMode;
  attachedInstances: AttachedInstance[];
  isAttached: boolean;
  labels: Record<string, string>;
  health: VolumeHealth;
  performanceMetrics?: PerformanceMetrics;
  encrypted: boolean;
  encryptionKeyId?: string;
  tridentBackend: string;
  snapshotCount: number;
  lastSnapshotAt?: number;
  createdAt: number;
  updatedAt: number;
}

// Volume Snapshot Interface
export interface VolumeSnapshot {
  id: string;
  name: string;
  volumeId: string;
  volumeName: string;
  status: SnapshotStatus;
  size: number; // GB
  createdAt: number;
  description?: string;
  isScheduled: boolean;
  scheduleId?: string;
  scheduleName?: string;
  restorable: boolean;
  labels: Record<string, string>;
}

// Snapshot Schedule Interface
export interface SnapshotSchedule {
  id: string;
  name: string;
  volumeId: string;
  volumeName: string;
  frequency: ScheduleFrequency;
  cronExpression?: string; // For custom schedules
  retentionCount: number; // Number of snapshots to keep
  retentionDays?: number; // Alternative: days to keep
  enabled: boolean;
  lastRun?: number;
  nextRun: number;
  createdAt: number;
  snapshotPrefix: string;
}

// Create Volume Payload
export interface CreateVolumePayload {
  name: string;
  size: number; // GB
  storageClassId: string;
  accessMode: AccessMode;
  volumeMode: VolumeMode;
  labels?: Record<string, string>;
  encrypted?: boolean;
  namespace?: string;
}

// Resize Volume Payload
export interface ResizeVolumePayload {
  volumeId: string;
  newSize: number; // GB (must be larger than current)
}

// Attach Volume Payload
export interface AttachVolumePayload {
  volumeId: string;
  instanceId: string;
  mountPath: string;
}

// Detach Volume Payload
export interface DetachVolumePayload {
  volumeId: string;
  instanceId: string;
}

// Clone Volume Payload
export interface CloneVolumePayload {
  sourceVolumeId: string;
  name: string;
  snapshotId?: string; // Optional: clone from specific snapshot
  storageClassId?: string; // Optional: different storage class
}

// Create Snapshot Payload
export interface CreateSnapshotPayload {
  volumeId: string;
  name: string;
  description?: string;
  labels?: Record<string, string>;
}

// Restore Snapshot Payload
export interface RestoreSnapshotPayload {
  snapshotId: string;
  restoreType: 'in-place' | 'new-volume';
  newVolumeName?: string; // Required if restoreType is 'new-volume'
  newStorageClassId?: string;
}

// Create Schedule Payload
export interface CreateSchedulePayload {
  volumeId: string;
  name: string;
  frequency: ScheduleFrequency;
  cronExpression?: string;
  retentionCount: number;
  retentionDays?: number;
  snapshotPrefix?: string;
}

// Volume Metrics History Point
export interface MetricsHistoryPoint {
  timestamp: number;
  iopsRead: number;
  iopsWrite: number;
  throughputRead: number;
  throughputWrite: number;
  latencyRead: number;
  latencyWrite: number;
  usedSize: number;
}

// Volume Activity Log Entry
export interface VolumeActivityLog {
  id: string;
  volumeId: string;
  action: 'created' | 'resized' | 'attached' | 'detached' | 'snapshot_created' | 'snapshot_restored' | 'cloned' | 'deleted' | 'health_changed';
  description: string;
  timestamp: number;
  userId?: string;
  userName?: string;
  metadata?: Record<string, unknown>;
}

// API Response Types
export interface ListVolumesResponse {
  volumes: Volume[];
  total: number;
}

export interface ListSnapshotsResponse {
  snapshots: VolumeSnapshot[];
  total: number;
}

export interface ListSchedulesResponse {
  schedules: SnapshotSchedule[];
  total: number;
}

export interface VolumeOperationResponse {
  success: boolean;
  message: string;
  volumeId?: string;
  snapshotId?: string;
}

// Filter Options
export interface VolumeFilterOptions {
  status?: VolumeStatus[];
  storageClassTier?: StorageClassTier[];
  accessMode?: AccessMode[];
  isAttached?: boolean;
  healthStatus?: VolumeHealthStatus[];
  search?: string;
}

// Cost Estimation
export interface VolumeCostEstimate {
  storageClass: StorageClass;
  sizeGB: number;
  monthlyStorageCost: number;
  estimatedIopsCost: number;
  totalMonthlyCost: number;
  yearlyProjection: number;
}
